import React, { useEffect, useState } from "react";
import { BarChart3, CheckCircle2, Circle, ExternalLink, RefreshCw, Unlink } from "lucide-react";
import { api } from "@/lib/api";
import { Row, IndexNowStat } from "../_ui";

/**
 * Cloudflare Web Analytics — per-site RUM beacon, privacy-first (no cookies).
 * Connect once per site; metrics aggregated over the last 7 days.
 */
export default function CloudflareAnalyticsPanel({ site }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'setup' | 'disconnect'
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/sites/${site.id}/analytics/status`);
        if (!cancelled) { setStatus(r.data); setLoading(false); }
      } catch (e) {
        if (!cancelled) {
          setFlash({ kind: "err", msg: e?.response?.data?.detail || "Failed to load analytics status" });
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [site.id]);

  async function reload() {
    try {
      const r = await api.get(`/sites/${site.id}/analytics/status`);
      setStatus(r.data);
    } catch (err) {
      setFlash({ kind: "err", msg: err?.response?.data?.detail || "Failed to refresh analytics status" });
    }
  }

  async function setup() {
    if (busy) return;
    setBusy("setup"); setFlash(null);
    try {
      const r = await api.post(`/sites/${site.id}/analytics/setup`);
      setFlash({ kind: "ok", msg: r.data.created ? "Analytics site created in Cloudflare" : "Linked to existing Cloudflare Analytics site" });
      await reload();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Setup failed" });
    } finally { setBusy(null); }
  }

  async function disconnect() {
    if (busy) return;
    if (!window.confirm("Disconnect Cloudflare Web Analytics? This won't delete historical data — it just stops the SSR beacon from firing on new requests.")) return;
    setBusy("disconnect"); setFlash(null);
    try {
      await api.post(`/sites/${site.id}/analytics/disconnect`);
      setFlash({ kind: "ok", msg: "Disconnected" });
      await reload();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Disconnect failed" });
    } finally { setBusy(null); }
  }

  if (loading) return <div className="text-xs text-zinc-500 mono pl-[200px]">Loading analytics status…</div>;
  if (!status) return <div className="text-xs text-rose-400 mono pl-[200px]">Failed to load analytics status</div>;

  const metrics = status.metrics;
  const hasDomain = !!status.domain;

  return (
    <div className="space-y-3" data-testid="cf-analytics-panel">
      <Row label="Status">
        <div className="flex items-center gap-2 flex-wrap">
          {status.connected ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-sm bg-emerald-900/40 border border-emerald-700 text-emerald-300 mono uppercase tracking-wider" data-testid="cf-analytics-badge-connected">
              <CheckCircle2 size={11}/> Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-sm bg-zinc-800 border border-zinc-700 text-zinc-400 mono uppercase tracking-wider" data-testid="cf-analytics-badge-disconnected">
              <Circle size={11}/> Not connected
            </span>
          )}
          {status.dashboard_url && (
            <a href={status.dashboard_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-emerald-400 hover:text-emerald-300 underline mono" data-testid="cf-analytics-dashboard-link">
              Open in Cloudflare <ExternalLink size={9}/>
            </a>
          )}
        </div>
      </Row>

      {!status.connected && (
        <>
          <Row label="">
            <button
              onClick={setup}
              disabled={busy || !hasDomain}
              data-testid="cf-analytics-setup-btn"
              className="inline-flex items-center gap-1.5 bg-orange-700 hover:bg-orange-600 disabled:opacity-40 text-white px-3 py-1.5 rounded-sm text-xs font-semibold mono"
            >
              <BarChart3 size={12}/> {busy === "setup" ? "Setting up…" : "Enable Web Analytics"}
            </button>
          </Row>
          {!hasDomain && (
            <div className="text-[11px] text-amber-400 mono pl-[200px]">
              Buy or link a custom domain in the Domain tab first.
            </div>
          )}
        </>
      )}

      {status.connected && (
        <>
          <Row label="Site tag">
            <div className="font-mono text-[11px] text-zinc-400 break-all" data-testid="cf-analytics-site-tag">{status.site_tag}</div>
          </Row>

          {status.metrics_error && (
            <Row label="Metrics">
              <div className="text-[11px] mono text-amber-400" data-testid="cf-analytics-metrics-error">
                {status.metrics_error}
              </div>
            </Row>
          )}

          {metrics && (
            <>
              <Row label="Last 7 days">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <IndexNowStat label="Page views" value={metrics.pageviews?.toLocaleString() ?? 0} testid="cf-analytics-stat-pageviews"/>
                  <IndexNowStat label="Visits" value={metrics.visits?.toLocaleString() ?? 0} testid="cf-analytics-stat-visits"/>
                  <IndexNowStat label="Pages tracked" value={(metrics.top_paths || []).length} testid="cf-analytics-stat-paths"/>
                </div>
              </Row>

              {(metrics.top_paths || []).length > 0 && (
                <Row label="Top pages">
                  <div className="space-y-1 text-xs mono" data-testid="cf-analytics-top-paths">
                    {(metrics.top_paths || []).slice(0, 5).map((row) => (
                      <div key={`path-${row.path}`} className="flex items-center gap-3">
                        <span className="text-zinc-500 w-7 text-right">{row.count.toLocaleString()}</span>
                        <span className="text-zinc-200 truncate">{row.path}</span>
                      </div>
                    ))}
                  </div>
                </Row>
              )}

              {(metrics.top_referrers || []).length > 0 && (
                <Row label="Top referrers">
                  <div className="space-y-1 text-xs mono" data-testid="cf-analytics-top-referrers">
                    {(metrics.top_referrers || []).slice(0, 5).map((row) => (
                      <div key={`ref-${row.referrer || 'direct'}`} className="flex items-center gap-3">
                        <span className="text-zinc-500 w-7 text-right">{row.count.toLocaleString()}</span>
                        <span className="text-zinc-200 truncate">{row.referrer || "(direct)"}</span>
                      </div>
                    ))}
                  </div>
                </Row>
              )}

              {(metrics.top_countries || []).length > 0 && (
                <Row label="Top countries">
                  <div className="space-y-1 text-xs mono" data-testid="cf-analytics-top-countries">
                    {(metrics.top_countries || []).slice(0, 5).map((row) => (
                      <div key={`country-${row.country}`} className="flex items-center gap-3">
                        <span className="text-zinc-500 w-7 text-right">{row.count.toLocaleString()}</span>
                        <span className="text-zinc-200 truncate">{row.country}</span>
                      </div>
                    ))}
                  </div>
                </Row>
              )}
            </>
          )}

          <Row label="Actions">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={reload}
                disabled={busy}
                data-testid="cf-analytics-refresh-btn"
                className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-3 py-1.5 rounded-sm text-xs mono disabled:opacity-40"
              >
                <RefreshCw size={12}/> Refresh
              </button>
              <button
                onClick={disconnect}
                disabled={busy}
                data-testid="cf-analytics-disconnect-btn"
                className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-rose-500 hover:text-rose-300 text-zinc-300 px-3 py-1.5 rounded-sm text-xs mono disabled:opacity-40"
              >
                <Unlink size={12}/> {busy === "disconnect" ? "…" : "Disconnect"}
              </button>
            </div>
          </Row>
        </>
      )}

      {flash && (
        <div className={`text-[11px] mono pl-[200px] ${flash.kind === "ok" ? "text-emerald-400" : "text-rose-400"}`} data-testid="cf-analytics-flash">
          {flash.msg}
        </div>
      )}

      <div className="text-[11px] text-zinc-500 mono pl-[200px]">
        Cloudflare Web Analytics — free, privacy-first (no cookies, GDPR-safe), beacon-based RUM. Auto-injected into every SSR page once connected. Metrics aggregated over last 7 days.
      </div>
    </div>
  );
}
